import * as React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory from "react-bootstrap-table2-filter";
import paginationFactory from "react-bootstrap-table2-paginator";
import overlayFactory from "react-bootstrap-table2-overlay";
import ContentLoader from "react-content-loader";

import i18n from "src/assets/messages";

import "./style.css";

const Loader = () => (
  <ContentLoader
    height={200}
    width={600}
    speed={2}
    primaryColor="#f3f3f3"
    secondaryColor="#ecebeb"
  >
    <rect x="0" y="20" rx="0" ry="0" width="20" height="20" />
    <rect x="0" y="60" rx="0" ry="0" width="20" height="20" />
    <rect x="0" y="100" rx="0" ry="0" width="20" height="20" />
    <rect x="0" y="140" rx="0" ry="0" width="20" height="20" />
    <rect x="0" y="180" rx="0" ry="0" width="20" height="20" />

    <rect x="50" y="20" rx="0" ry="0" width="163" height="20" />
    <rect x="50" y="60" rx="0" ry="0" width="163" height="20" />
    <rect x="50" y="100" rx="0" ry="0" width="163" height="20" />
    <rect x="50" y="140" rx="0" ry="0" width="163" height="20" />
    <rect x="50" y="180" rx="0" ry="0" width="163" height="20" />

    <rect x="243" y="20" rx="0" ry="0" width="163" height="20" />
    <rect x="243" y="60" rx="0" ry="0" width="163" height="20" />
    <rect x="243" y="100" rx="0" ry="0" width="163" height="20" />
    <rect x="243" y="140" rx="0" ry="0" width="163" height="20" />
    <rect x="243" y="180" rx="0" ry="0" width="163" height="20" />

    <rect x="436" y="20" rx="0" ry="0" width="163" height="20" />
    <rect x="436" y="60" rx="0" ry="0" width="163" height="20" />
    <rect x="436" y="100" rx="0" ry="0" width="163" height="20" />
    <rect x="436" y="140" rx="0" ry="0" width="163" height="20" />
    <rect x="436" y="180" rx="0" ry="0" width="163" height="20" />
  </ContentLoader>
);

interface Props {
  datas: any;
  noDataIndication?: any;
  columns: Array<{
    dataField: string;
    text: string;
    classes?: string;
    headerClasses: string | null;
    headerStyle?: any;
    formatter: any;
    filter?: any;
    filterRenderer?: (onFilter: any, column: any) => React.ReactNode;
  }>;
  selectColumn?: boolean;
  onSelected?: (values: any[], ids?: string[]) => void;
  striped?: boolean;
  hover?: boolean;
  rowClasses?: string;
  containerClass?: string;
  classes?: string;
  condensed?: boolean;
  bordered?: boolean;
  handleTableChange?: any;
  pagination?:
  | boolean
  | {
    page?: number;
    hideSizePerPage?: boolean;
    onPageChange?: any;
    sizePerPage?: number;
    alwaysShowAllBtns?: boolean;
    nextPageText?: string;
    prePageText?: string;
    sizePerPageList?: Array<{
      value: number;
    }>;
  };
  ready: boolean;
  totalSize: number;
  hideHeader?: boolean;
  loading: boolean;
}

interface State {
  selected: string[] | null;
  elementSelected: any;
}

export default class Columns extends React.Component<Props, State> {
  public static defaultProps: Partial<Props> = {
    hideHeader: false,
  };

  state = {
    selected: [] as string[],
    elementSelected: [],
  };

  /**
   * Display the main content
   */
  render() {
    const hideSelectColumn =
      this.props.selectColumn === undefined ? false : !this.props.selectColumn;
    const selectRow = {
      mode: "checkbox",
      clickToSelect: false,
      hideSelectColumn,
      selected: this.state.selected,
      onSelectAll: (isSelect: boolean, results: any[]) => {
        if (isSelect) {
          this.setState(
            {
              selected: results.map((el: any) => el.id, []),
              elementSelected: results,
            },
            () => {
              if (this.props.onSelected) {
                this.props.onSelected(this.state.elementSelected);
              }
            }
          );
        }
        else {
          this.setState({
            selected: [], elementSelected: []
          },
          () => {
            if (this.props.onSelected) {
              this.props.onSelected([]);
            }
          })
        }
      },
      onSelect: (row: any) => {
        // We need to add the id rather than the object in the state.selected
        // so that react-bootstrap-tables can display the
        // selected lines or the unselected lines
        // We send the elementSelected to the onSelected so that it contains
        // the object
        const element: string = row.id;
        const index: number = this.state.selected.indexOf(element);

        // If the selected line is not in the Editor then add them
        if (index === -1) {
          this.setState(
            {
              selected: [...this.state.selected, row.id],
              elementSelected: [...this.state.elementSelected, row],
            },
            () => {
              this.props.onSelected!(
                this.state.elementSelected,
                this.state.selected
              );
            }
          );
        } else {
          // If the selected line is in the Editor then remove them
          this.setState(
            {
              selected: this.state.selected.filter((el: any) => el !== row.id),
              elementSelected: this.state.elementSelected.filter(
                (el: any) => el.id !== row.id
              ),
            },
            () => {
              if (this.props.onSelected) {
                this.props.onSelected(
                  this.state.elementSelected,
                  this.state.selected
                );
              }
            }
          );
        }
      },
    };

    let paginationOptions: any = {
      page: 1,
    };
    if (this.props.pagination) {
      paginationOptions = {
        hideSizePerPage: false,
        alwaysShowAllBtns: false,
        showTotal: true,
        sizePerPage: 10,
        totalSize: this.props.totalSize,
        sizePerPageList: [
          {
            value: 10,
          },
        ],
      };

      if (typeof this.props.pagination === "object") {
        paginationOptions = { ...paginationOptions, ...this.props.pagination };
      }
    }

    return (
      <div className={this.props.containerClass || "table"}>
        {this.props.ready ? (
          <BootstrapTable
            classes={this.props.classes || "tableCustom"}
            rowClasses={this.props.rowClasses || "tableRow"}
            keyField="id"
            data={this.props.datas}
            columns={this.props.columns}
            noDataIndication={
              this.props.noDataIndication || i18n.t("resultEmpty")
            }
            remote={{
              pagination: !this.props.pagination ? false : true,
              filter: true,
              sort: true,
            }}
            onTableChange={
              this.props.handleTableChange === undefined
                ? undefined
                : this.props.handleTableChange
            }
            selectRow={selectRow}
            striped={
              this.props.striped === undefined ? true : this.props.striped
            }
            hover={this.props.hover === undefined ? true : this.props.hover}
            bordered={
              this.props.bordered === undefined ? true : this.props.bordered
            }
            condensed={
              this.props.condensed === undefined ? true : this.props.condensed
            }
            pagination={
              !this.props.pagination
                ? undefined
                : paginationFactory(paginationOptions)
            }
            headerClasses={this.props.hideHeader ? "hideHeader" : undefined}
            filter={filterFactory()}
            loading={this.props.loading}
            overlay={overlayFactory({
              spinner: true,
              spinnerSize: "40px",
              color: "#086ad8",
              background: "rgba(192,192,192,0.3)",
            })}
          />
        ) : (
          <Loader />
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/presentations/collections/Table/Columns/index.tsx